import type { Vec2 } from "../world/geometry";

const DEADZONE = 0.15;

export const enum GPButton {
  A = 0,
  B = 1,
  X = 2,
  Y = 3,
  L = 4,
  R = 5,
  ZL = 6,
  ZR = 7,
  Select = 8,
  Start = 9,
  DpadUp = 12,
  DpadDown = 13,
  DpadLeft = 14,
  DpadRight = 15,
  Home = 16,
  Capture = 17,
}

export interface GamepadState {
  readonly movement: Vec2;
  readonly connected: boolean;
  readonly pressed: ReadonlySet<GPButton>;
}

export class GamepadInput {
  #movement: Vec2 = { x: 0, y: 0 };
  #connected = false;
  #pressed = new Set<GPButton>();
  #prevPressed = new Set<GPButton>();
  #gamepadIndex: number | null = null;

  get connected(): boolean {
    return this.#connected;
  }

  get current(): GamepadState {
    return { movement: this.#movement, connected: this.#connected, pressed: this.#pressed };
  }

  wasPressed(button: GPButton): boolean {
    return this.#pressed.has(button) && !this.#prevPressed.has(button);
  }

  /** Called from the `gamepadconnected` event to register which slot to poll. */
  onConnected(index: number): void {
    this.#gamepadIndex = index;
  }

  /** Called from the `gamepaddisconnected` event. */
  onDisconnected(index: number): void {
    if (this.#gamepadIndex === index) {
      this.#gamepadIndex = null;
      this.#connected = false;
      this.#movement = { x: 0, y: 0 };
      this.#pressed = new Set();
    }
  }

  poll(): GamepadState {
    const gamepads = navigator.getGamepads?.() ?? [];

    // Prefer the tracked index; fall back to first available.
    let gp: Gamepad | null = null;
    if (this.#gamepadIndex !== null) {
      gp = gamepads[this.#gamepadIndex] ?? null;
    }
    if (!gp) {
      for (let i = 0; i < gamepads.length; i++) {
        const pad = gamepads[i];
        if (pad) {
          gp = pad;
          this.#gamepadIndex = pad.index;
          break;
        }
      }
    }

    this.#prevPressed = new Set(this.#pressed);

    if (!gp) {
      this.#connected = false;
      this.#movement = { x: 0, y: 0 };
      this.#pressed = new Set();
      return this.current;
    }

    this.#connected = true;

    let dx = gp.axes[0] ?? 0;
    let dy = gp.axes[1] ?? 0;

    if (Math.hypot(dx, dy) < DEADZONE) {
      dx = 0;
      dy = 0;
    }

    const dpadUp = gp.buttons[12]?.pressed ?? false;
    const dpadDown = gp.buttons[13]?.pressed ?? false;
    const dpadLeft = gp.buttons[14]?.pressed ?? false;
    const dpadRight = gp.buttons[15]?.pressed ?? false;

    if (dpadUp) dy = -1;
    if (dpadDown) dy = 1;
    if (dpadLeft) dx = -1;
    if (dpadRight) dx = 1;

    this.#movement = { x: dx, y: dy };

    this.#pressed = new Set();
    for (let i = 0; i < gp.buttons.length; i++) {
      if (gp.buttons[i]?.pressed) {
        this.#pressed.add(i as GPButton);
      }
    }

    return this.current;
  }
}
